import { useState } from 'react'
import {
  useAddCalibration,
  useDeleteInstrument,
  useInstrument,
  useInstruments,
  useSaveInstrument,
  useUsers
} from '../api/queries'
import type { Instrument } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'

/** yyyy-MM-dd ud af en ISO-streng, til datofelter. */
function isoDate(value?: string | null) {
  return value ? value.slice(0, 10) : ''
}

function formatDate(value?: string | null) {
  if (!value) return '-'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Hvor mange dage til kalibreringen forfalder. Negativt = overskredet. */
function daysUntil(value?: string | null) {
  if (!value) return null
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  const due = new Date(y, m - 1, d)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((due.getTime() - today.getTime()) / 86400000)
}

/**
 * Måleinstrumenterne og deres kalibrering (beslutning 047).
 *
 * Alle må se listen - montøren skal kunne slå op, om det instrument, han har i
 * bilen, stadig er gyldigt. Kun administratorer kan oprette, rette og skrive en
 * kalibrering ind: det svarer til et certifikat fra et laboratorium og er ikke
 * noget, man selv bestemmer.
 */
export function InstrumentsPage() {
  const [search, setSearch] = useState('')
  const [dueOnly, setDueOnly] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<Instrument | null | undefined>(undefined)
  const [calibrating, setCalibrating] = useState<Instrument | null>(null)
  const [open, setOpen] = useState<number | null>(null)

  const { isAdmin } = useAuth()
  const { data, isLoading, error } = useInstruments({ search, dueOnly, includeInactive })

  const expired = data?.filter((i) => i.isExpired).length ?? 0
  const uncalibrated = data?.filter((i) => !i.isCalibrated && i.calibrationIntervalMonths).length ?? 0

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {data?.length ?? 0} {(data?.length ?? 0) === 1 ? 'instrument' : 'instrumenter'}
        </div>
        <input
          placeholder="Søg i navn, type eller serienummer ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} />
          <span>Forfalder snart</span>
        </label>
        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          <span>Vis udtjente</span>
        </label>
        {isAdmin && (
          <button className="btn btn--sm btn--primary" onClick={() => setEditing(null)}>
            Nyt instrument
          </button>
        )}
      </div>

      {/* Et udløbet instrument er ikke en detalje: måler man med det, kan
          eftersynet ikke godkendes. Derfor står det øverst. */}
      {(expired > 0 || uncalibrated > 0) && (
        <div className="map-warning">
          {expired > 0 && (
            <>
              <strong>
                {expired === 1 ? 'Ét instrument har en udløbet kalibrering' : `${expired} instrumenter har en udløbet kalibrering`}.
              </strong>{' '}
            </>
          )}
          {uncalibrated > 0 && (
            <>
              {uncalibrated === 1 ? 'Ét instrument er aldrig kalibreret' : `${uncalibrated} instrumenter er aldrig kalibreret`}.{' '}
            </>
          )}
          Måler man med dem, kan et eftersyn ikke godkendes - måleværdierne kan ikke lægges til grund.
        </div>
      )}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data && data.length === 0 && (
        <EmptyState>
          Ingen instrumenter fundet. {isAdmin && 'Opret det første med knappen "Nyt instrument".'}
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <Card>
          <div className="table-wrap">
            <table className="cal-table">
              <thead>
                <tr>
                  <th>Instrument</th>
                  <th>Serienummer</th>
                  <th>Hos</th>
                  <th>Kalibreret</th>
                  <th>Næste</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((i) => {
                  const days = daysUntil(i.nextCalibrationDate)
                  return (
                    <tr key={i.id} style={{ opacity: i.isActive ? 1 : 0.55 }}>
                      <td>
                        <button className="link-button" onClick={() => setOpen(open === i.id ? null : i.id)}>
                          {i.name}
                        </button>
                        <div className="tiny muted">
                          {[i.instrumentType, i.manufacturer, i.model].filter(Boolean).join(' · ') || '-'}
                          {!i.isActive && ' · udtjent'}
                        </div>
                      </td>
                      <td className="small">{i.serialNumber ?? '-'}</td>
                      <td className="small">{i.assignedUserName ?? i.location ?? 'Værkstedet'}</td>
                      <td className="small">{formatDate(i.lastCalibrationDate)}</td>
                      <td className="small">
                        {i.calibrationIntervalMonths ? (
                          <>
                            <span className={i.isExpired ? 'overdue' : ''}>
                              {formatDate(i.nextCalibrationDate)}
                            </span>
                            {days !== null && (
                              <div className={`tiny ${i.isExpired ? 'overdue' : 'muted'}`}>
                                {i.isExpired
                                  ? `overskredet med ${Math.abs(days)} ${Math.abs(days) === 1 ? 'dag' : 'dage'}`
                                  : `om ${days} ${days === 1 ? 'dag' : 'dage'}`}
                              </div>
                            )}
                            {!i.isCalibrated && <div className="tiny overdue">aldrig kalibreret</div>}
                          </>
                        ) : (
                          <span className="muted">kalibreres ikke</span>
                        )}
                      </td>
                      <td>
                        {isAdmin && (
                          <div className="btn-group">
                            <button className="btn btn--sm" onClick={() => setCalibrating(i)}>
                              Kalibrering
                            </button>
                            <button className="btn btn--sm" onClick={() => setEditing(i)}>
                              Ret
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {open !== null && <History instrumentId={open} onClose={() => setOpen(null)} />}
      {editing !== undefined && <InstrumentForm instrument={editing} onClose={() => setEditing(undefined)} />}
      {calibrating && <CalibrationForm instrument={calibrating} onClose={() => setCalibrating(null)} />}
    </>
  )
}

/** Kalibreringshistorikken. Den skal kunne fremvises - ikke bare den nyeste dato. */
function History({ instrumentId, onClose }: { instrumentId: number; onClose: () => void }) {
  const { data, isLoading } = useInstrument(instrumentId)

  return (
    <Card
      title={data ? `Kalibreringer - ${data.instrument.name}` : 'Kalibreringer'}
      actions={<button className="btn btn--sm" onClick={onClose}>Luk</button>}
    >
      {isLoading && <Loading />}
      {data && data.calibrations.length === 0 && (
        <EmptyState>Instrumentet er aldrig kalibreret.</EmptyState>
      )}
      {data && data.calibrations.length > 0 && (
        <div className="table-wrap">
          <table className="cal-table">
            <thead>
              <tr>
                <th>Udført</th>
                <th>Gyldig til</th>
                <th>Af</th>
                <th>Certifikat</th>
                <th>Skrevet ind af</th>
              </tr>
            </thead>
            <tbody>
              {data.calibrations.map((c) => (
                <tr key={c.id}>
                  <td className="small">{formatDate(c.calibratedOn)}</td>
                  <td className="small">{formatDate(c.validUntil)}</td>
                  <td className="small">{c.performedBy ?? '-'}</td>
                  <td className="small">{c.certificateNumber ?? '-'}</td>
                  <td className="small muted">{c.registeredByName ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.notes && <div className="small muted" style={{ marginTop: 10 }}>{data.notes}</div>}
    </Card>
  )
}

function InstrumentForm({ instrument, onClose }: { instrument: Instrument | null; onClose: () => void }) {
  const { data: users } = useUsers()
  const save = useSaveInstrument()
  const remove = useDeleteInstrument()
  const [error, setError] = useState<string | null>(null)

  const [form, setForm] = useState({
    name: instrument?.name ?? '',
    instrumentType: instrument?.instrumentType ?? '',
    manufacturer: instrument?.manufacturer ?? '',
    model: instrument?.model ?? '',
    serialNumber: instrument?.serialNumber ?? '',
    assignedUserId: instrument?.assignedUserId ? String(instrument.assignedUserId) : '',
    location: instrument?.location ?? '',
    calibrationIntervalMonths: instrument?.calibrationIntervalMonths
      ? String(instrument.calibrationIntervalMonths)
      : '12',
    notes: '',
    isActive: instrument?.isActive ?? true
  })

  function update(field: keyof typeof form, value: string | boolean) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await save.mutateAsync({
        id: instrument?.id,
        body: {
          name: form.name,
          instrumentType: form.instrumentType || null,
          manufacturer: form.manufacturer || null,
          model: form.model || null,
          serialNumber: form.serialNumber || null,
          assignedUserId: form.assignedUserId ? Number(form.assignedUserId) : null,
          location: form.location || null,
          calibrationIntervalMonths: form.calibrationIntervalMonths
            ? Number(form.calibrationIntervalMonths)
            : null,
          notes: form.notes || null,
          isActive: form.isActive
        }
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Instrumentet kunne ikke gemmes.')
    }
  }

  async function del() {
    if (!instrument) return
    if (!window.confirm(`Slet "${instrument.name}"?`)) return
    setError(null)
    try {
      await remove.mutateAsync(instrument.id)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Instrumentet kunne ikke slettes.')
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal__card modal__card--wide">
        <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>
          {instrument ? 'Ret instrument' : 'Nyt instrument'}
        </h2>
        <form onSubmit={submit}>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">Navn *</span>
              <input required placeholder="f.eks. Fluke 1663" value={form.name} onChange={(e) => update('name', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Type</span>
              <input
                placeholder="f.eks. Installationstester"
                value={form.instrumentType}
                onChange={(e) => update('instrumentType', e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Fabrikat</span>
              <input value={form.manufacturer} onChange={(e) => update('manufacturer', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Model</span>
              <input value={form.model} onChange={(e) => update('model', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Serienummer</span>
              <input value={form.serialNumber} onChange={(e) => update('serialNumber', e.target.value)} />
              <span className="tiny muted">Står på kalibreringscertifikatet - uden det kan papiret ikke bindes til instrumentet.</span>
            </label>
            <label className="field">
              <span className="field__label">Udleveret til</span>
              <select value={form.assignedUserId} onChange={(e) => update('assignedUserId', e.target.value)}>
                <option value="">Værkstedet</option>
                {users?.map((u) => (
                  <option key={u.id} value={u.id}>{u.displayName}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Placering</span>
              <input placeholder="f.eks. Bil 3" value={form.location} onChange={(e) => update('location', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Kalibreres hver (måneder)</span>
              <input
                type="number"
                min={1}
                max={120}
                placeholder="Tom = kalibreres ikke"
                value={form.calibrationIntervalMonths}
                onChange={(e) => update('calibrationIntervalMonths', e.target.value)}
              />
              <span className="tiny muted">Lad feltet stå tomt for udstyr, der ikke skal kalibreres.</span>
            </label>
            <label className="field" style={{ gridColumn: '1 / -1' }}>
              <span className="field__label">Noter</span>
              <input value={form.notes} onChange={(e) => update('notes', e.target.value)} />
            </label>
            <label className="cal-person" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isActive} onChange={(e) => update('isActive', e.target.checked)} />
              <span>Aktivt</span>
            </label>
          </div>

          {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

          <div className="btn-group">
            <button className="btn btn--primary" type="submit" disabled={save.isPending || !form.name}>
              {save.isPending ? 'Gemmer ...' : 'Gem'}
            </button>
            <button className="btn" type="button" onClick={onClose}>Fortryd</button>
            {instrument && (
              <button className="btn btn--danger" type="button" disabled={remove.isPending} onClick={del}>
                Slet
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

function CalibrationForm({ instrument, onClose }: { instrument: Instrument; onClose: () => void }) {
  const add = useAddCalibration(instrument.id)
  const [error, setError] = useState<string | null>(null)

  const [form, setForm] = useState({
    calibratedOn: isoDate(new Date().toISOString()),
    validUntil: '',
    performedBy: '',
    certificateNumber: '',
    notes: ''
  })

  function update(field: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await add.mutateAsync({
        calibratedOn: new Date(form.calibratedOn).toISOString(),
        validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : null,
        performedBy: form.performedBy || null,
        certificateNumber: form.certificateNumber || null,
        notes: form.notes || null
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kalibreringen kunne ikke gemmes.')
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal__card">
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Registrér kalibrering</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          {instrument.name}
          {instrument.serialNumber ? ` · ${instrument.serialNumber}` : ''}
        </p>
        <form onSubmit={submit}>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">Udført *</span>
              <input type="date" required value={form.calibratedOn} onChange={(e) => update('calibratedOn', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Gyldig til</span>
              <input type="date" value={form.validUntil} onChange={(e) => update('validUntil', e.target.value)} />
              <span className="tiny muted">
                Tom = regnes ud fra intervallet
                {instrument.calibrationIntervalMonths ? ` (${instrument.calibrationIntervalMonths} mdr.)` : ''}.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Udført af</span>
              <input placeholder="Laboratorium eller firma" value={form.performedBy} onChange={(e) => update('performedBy', e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Certifikatnummer</span>
              <input value={form.certificateNumber} onChange={(e) => update('certificateNumber', e.target.value)} />
            </label>
            <label className="field" style={{ gridColumn: '1 / -1' }}>
              <span className="field__label">Noter</span>
              <input value={form.notes} onChange={(e) => update('notes', e.target.value)} />
            </label>
          </div>

          {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

          <div className="btn-group">
            <button className="btn btn--primary" type="submit" disabled={add.isPending}>
              {add.isPending ? 'Gemmer ...' : 'Gem kalibrering'}
            </button>
            <button className="btn" type="button" onClick={onClose}>Fortryd</button>
          </div>
        </form>
      </div>
    </div>
  )
}
